import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Flex, Row, Space } from 'antd';
import AIProgressBar from 'components/Message/CreateMessageComponent/StepMessage/AiCreation/AIProgressBar/AIProgressBar';
import { useMessageService } from 'components/Message/useMessageService';
import { useEffect, useState } from 'react';
import './AiCreationTone.css';

type AllProps = {
  onBack: () => void;
  onSave: (tones: string[], shouldExit?: boolean) => void;
};

const toneOptions = [
  'Warm & Loving',
  'Honest & Direct',
  'Lighthearted / Humorous',
  'Proud & Encouraging',
  'Reflective / Spiritual',
  'Grateful & Appreciative',
  'Comforting & Reassuring',
  'Nostalgic & Reminiscent',
  'Guiding & Wise',
  'Formal & Respectful'
];

function AiCreationTone({ onBack, onSave }: AllProps) {
  const { message, updateMessageAiFields } = useMessageService();
  const [tones, setTones] = useState<string[]>([]);

  const handleSubmit = async (shouldExit = false) => {
    await updateMessageAiFields({ aiTones: tones });
    onSave(tones, shouldExit);
  };

  useEffect(() => {
    setTones(message?.aiTones || []);
  }, [message?.aiTones]);

  return (
    <div className="ai-creation-tone create-message-component-full-height">
      <div>
        <AIProgressBar currentStep={1} />
        <div className="create-message-component-title">Which tone(s) should this message have?</div>
        <div className="create-message-component-subtitle">Choose any mix that feels right.</div>

        <Row>
          <Col sm={12} xs={24}>
            <label className="tone-label">Select tones (choose one or more)</label>
            <Checkbox.Group value={tones} onChange={setTones} style={{ width: '100%', marginTop: '16px' }}>
              <Space size="small" direction="vertical">
                {toneOptions.map((option) => (
                  <Checkbox key={option} value={option}>
                    {option}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Col>
          <Col sm={12} xs={0}>
            <div className="tone-preview">
              <h4>Preview</h4>
              <div className="tone-descriptions">
                {tones.length === 0 ? (
                  <div className="tone-description-item">
                    <p className="tone-description">Preview — Select one or more tones to see descriptions.</p>
                  </div>
                ) : (
                  tones.map((selectedTone) => (
                    <div key={selectedTone} className="tone-description-item">
                      <strong>{selectedTone}:</strong>
                      <p className="tone-description">
                        {selectedTone === 'Warm & Loving' && 'Gentle, heartfelt, and caring.'}
                        {selectedTone === 'Honest & Direct' && 'Plainspoken, sincere, no frills.'}
                        {selectedTone === 'Lighthearted / Humorous' && 'Playful; aims for a smile.'}
                        {selectedTone === 'Proud & Encouraging' &&
                          'Affirms pride and belief; motivates for the future.'}
                        {selectedTone === 'Reflective / Spiritual' && 'Quiet, introspective; offers hope or faith.'}
                        {selectedTone === 'Grateful & Appreciative' && 'Thanks for support and shared time.'}
                        {selectedTone === 'Comforting & Reassuring' && 'Soothes grief; steady and calming.'}
                        {selectedTone === 'Nostalgic & Reminiscent' && 'Looks back on shared memories.'}
                        {selectedTone === 'Guiding & Wise' && "Shares lessons or advice for what's ahead."}
                        {selectedTone === 'Formal & Respectful' &&
                          'Polished and measured; good for professional distance.'}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Row className="create-message-component-footer">
        <Col span={12}>
          <Button onClick={onBack} type="link">
            <ArrowLeftOutlined /> Back
          </Button>
        </Col>
        <Col span={12} className="text-right">
          <Flex gap={20} justify="flex-end">
            <Button onClick={() => handleSubmit(true)}>Save & Exit</Button>
            <Button type="primary" onClick={() => handleSubmit()} disabled={tones.length === 0}>
              Next
            </Button>
          </Flex>
        </Col>
      </Row>
    </div>
  );
}

export default AiCreationTone;
